import { useEffect, useRef, useState } from 'react';
import { api, datum } from '../api.ts';
import { grubenRadius, type Kartenlage, type Punkt }
  from '../teile/Tiefbaukarte.tsx';
import Kartenflaeche from '../teile/Kartenflaeche.tsx';
import { START_LAGEPLAN, type Kartenschalter }
  from '../teile/kartenstart.ts';
import KeinePunkte from '../teile/KeinePunkte.tsx';
import Adresssuche from '../teile/Adresssuche.tsx';
import Punktgenauigkeit from '../teile/Punktgenauigkeit.tsx';
import { Dialog } from '../teile/Formular.tsx';
import { lageplanZahlen, meterText } from '../teile/grabenlaenge.ts';

/**
 * Die Karte für den Lageplan, der an die Gemeinde geht.
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „beim auftrag vorbereiten fehlen die karten, die Grundstücken, bw
 *    flurstücke und das ausblenden der luftbilder. außerdem kann der Lap_Maps
 *    nciht erstellt werden, da fehlt alles."
 *
 * ORIGINALTEXT MARVIN (20.08.2026)
 *   „sehr gut und nun bauen wir die karte so mit den ganzen funktionen,
 *    flurstück usw auch auf die karte auftrag vorbereiten."
 *
 * WAS HIER GEZEICHNET WIRD: eine Montagegrube als Kreis, ein Graben als Linie,
 * beides in Acrobat-Rot. Genau das hat der Innendienst bisher von Hand in
 * Acrobat gemacht, an 21 von 23 geprüften Altfällen. Die Markierung ist
 * symbolisch und nicht vermessen; ein Klick auf die Karte ist also genauso
 * genau wie bisher.
 *
 * NICHT ZU VERWECHSELN MIT DER TIEFBAUPLANUNG. Marvin am 19.08.2026: „es hat
 * nichts mit dem lap_maps zu tun ... Das andere ist nur intern für uns". Die
 * Planung läuft bis ins Haus, über Privatgrund. Dieses Blatt zeigt den
 * öffentlichen Teil.
 */

export interface Markierung {
  art: 'grube' | 'graben';
  punkte: Punkt[];
}

type Werkzeug = 'grube' | 'graben';

export default function Lageplankarte({
  auftragId, breite = 820, hoehe = 520, aufAenderung,
}: {
  auftragId: string;
  /**
   * Das WUNSCHMASS der Karte, nicht ihre Grösse.
   *
   * Gemessen wird die Spalte, in der sie steht (`Kartenflaeche` hält den
   * Messfühler); diese Zahlen sind der Deckel und das Seitenverhältnis. Eine
   * Karte wird nicht grösser als gedacht, nur kleiner - ein 4K-Schirm zöge
   * sie sonst auf zweitausend Bildpunkte und holte viermal so viel Bild von
   * einem fremden Dienst.
   */
  breite?: number;
  hoehe?: number;
  /** Meldet dem Formular, dass sich etwas geändert hat. */
  aufAenderung?: () => void;
}) {
  const [d, setD] = useState<any>(null);
  const [lage, setLage] = useState<Kartenlage | null>(null);
  const [mark, setMark] = useState<Markierung[]>([]);
  /**
   * WAS WIRKLICH GESPEICHERT IST - und das ist die Antwort auf „alte Kreise".
   *
   * ORIGINALTEXT MARVIN (03.09.2026)
   *   „es sind «alte kreise» die ich mal gezeichnet habe in dem ganzen
   *    testvorgang irgendwo gecacht und werden immer mit angezeigt."
   *
   * **SIE SIND NICHT ZWISCHENGESPEICHERT, SONDERN GESPEICHERT.** Sie stehen
   * am Lageplan DIESES Auftrags (`lageplan.markierungen`) und werden bei
   * jedem Öffnen wieder gezeichnet - genau dafür sind sie da. Was fehlte,
   * war der Satz daneben: „zurück" und „Alles weg" ändern zunächst nur das
   * Bild auf dem Schirm, gültig wird es erst mit „Ausschnitt merken" oder
   * „LAP_Maps erzeugen".
   *
   * **Eine Zeichnung, die aussieht wie ein Rest im Browser, wird zum
   * Fehler gemeldet** - und wer sie für einen Rest hält, sucht ihn dort, wo
   * er nicht ist.
   */
  const [gespeichert, setGespeichert] = useState<Markierung[]>([]);
  /* DIE SUCHE AUCH MIT PUNKT (.451). ORIGINALTEXT MARVIN (22.10.2026):
     „Und wir hatten doch mal eine manuelle eingabe, diese wird garnicht mehr
      angezeigt." Sie stand nur da, wenn KEIN Punkt da war; ein falscher Punkt
      (Dorfstr. 7, 53505: Burgsahr statt Altenburg) ließ sich nicht mehr
      berichtigen. */
  const [ortSuchen, setOrtSuchen] = useState(false);
  /** Steht die Rückfrage vor dem wirklichen Löschen offen? */
  const [loeschfrage, setLoeschfrage] = useState(false);
  const [werkzeug, setWerkzeug] = useState<Werkzeug>('grube');
  /**
   * DER STAND DER SCHALTER, ZUM LESEN.
   *
   * ORIGINALTEXT MARVIN (04.09.2026)
   *   „beide Bereiche ... hat die gleiche kartenstruktur mit allem. es
   *    unterscheidet sich nur in dem was markiert ist beim starten und die
   *    symbole zur anzeichnung!"
   *
   * Hier standen vier eigene Merker (`luftbild`, `schrift`, `grenzen`,
   * `nummern`) und daneben, in der Planung, dieselben vier noch einmal.
   * **Gehalten werden sie jetzt EINMAL, in `teile/Kartenflaeche.tsx`.** Was
   * hier steht, ist eine Abschrift zum LESEN: „Ausschnitt merken" und
   * „LAP_Maps erzeugen" schreiben `luftbild` und `flurstuecke` mit, also
   * müssen sie die Werte kennen. Sie entscheiden nichts - die Quelle der
   * Wahrheit ist der Schalter, nicht diese Zeile.
   */
  const [schalter, setSchalter] = useState<Kartenschalter>(START_LAGEPLAN);
  /* WAS DAS HOLEN DER GRENZEN GERADE TUT. Geholt werden sie in
     `Kartenflaeche`; hier steht nur, was darunter zu lesen ist - ein Dienst,
     der zwanzig Sekunden braucht, sieht ohne diese Zeile aus wie einer, der
     nichts hat. */
  const [flurstand, setFlurstand] =
    useState<{ laeuft: boolean; meldung: string | null }>(
      { laeuft: false, meldung: null });
  const [laeuft, setLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  // Die Umgebungsbilder. Erst ansehen, dann ablegen: der Grund steht im
  // Bestand, vier Ansichten aus dem Inneren eines Blumenladens.
  const [umg, setUmg] = useState<any>(null);
  const [umgLaeuft, setUmgLaeuft] = useState(false);

  useEffect(() => {
    api.lageplan(auftragId).then((r) => {
      setD(r);
      const geladen: Markierung[] =
        Array.isArray(r.markierungen) ? r.markierungen : [];
      setMark(geladen);
      setGespeichert(geladen);
      /* HIER STANDEN ZWEI ZEILEN, DIE DEN ABDRUCK ZURÜCKGEHOLT HABEN:
         `setLuftbild(r.luftbild !== false)` und `setGrenzen(r.flurstuecke ===
         true)`. Beide sind weg, und die Begründung steht oben an den beiden
         Merkern: die Ansicht beginnt mit Luftbild AN und Grenzen AUS, jedes
         Mal. Was auf das Blatt kommt, entscheidet weiter der Stand in diesem
         Fenster. */
      /* DIE STUFE KOMMT VOM SERVER UND STEHT HIER NICHT NOCH EINMAL.
         Hier stand `?? 19` - die dritte von vier Fassungen derselben Zahl,
         und die Planung nebenan trug `?? 20`. Der ganze Befund steht in
         `api/src/kartenstufe.ts`. */
      if (r.mitte) setLage({ mitte: r.mitte, zoom: r.zoom });
    }).catch((f: any) => setFehler(f?.fehler ?? String(f)));
  }, [auftragId]);

  if (fehler) return <div className="hinweis warnung">{fehler}</div>;
  if (!d) return <p className="klein leise">Die Karte wird geladen.</p>;

  if (!d.mitte || !lage) {
    /* HIER STAND EIN SATZ UND SONST NICHTS.
     *
     * Er nannte zwei Wege („mit dem nächsten Abgleich", „in den Stammdaten
     * eintragen") und beide gab es nicht: die Übernahme hat `lat`/`lon` nie
     * geschrieben, die Stammdaten haben kein Feld dafür, und eine Suche gab
     * es im Neubau überhaupt nicht. Wer den Weg gesucht hat, hat ihn nicht
     * gefunden.
     *
     * Jetzt steht der Weg AN DER STELLE, an der die Frage entsteht: suchen,
     * und wenn der Dienst die Hausnummer nicht kennt, eine Nachbarnummer
     * derselben Straße eintragen. Der Satz „von Hand eintragen" stand hier bis
     * .161 und meinte zwei Zahlen; die gibt es nicht mehr, und ein Hinweis auf
     * einen Weg, den es nicht gibt, ist schlimmer als kein Hinweis. */
    return (
      <KeinePunkte
        auftragId={auftragId} text={d.ohneMitte}
        anschrift={d.anschrift}
        fertig={() => {
          // NEU LADEN, NICHT NUR DIE MITTE SETZEN. Mit dem Punkt kommen
          // Flurstuecke, Luftbild und der ganze Rest, und das entscheidet der
          // Server.
          api.lageplan(auftragId).then((r) => {
            setD(r);
            if (r.mitte) setLage({ mitte: r.mitte, zoom: r.zoom });
          }).catch(() => {});
          aufAenderung?.();
        }} />
    );
  }

  function setzen(p: Punkt) {
    setMeldung(null);
    if (werkzeug === 'grube') {
      setMark((m) => [...m, { art: 'grube', punkte: [p] }]);
    } else {
      setMark((m) => {
        const letzte = m[m.length - 1];
        // Klick für Klick weiter: der zweite Klick verlängert den Graben, statt
        // einen neuen anzufangen. „Neuer Graben" ist ein eigener Knopf.
        if (letzte?.art === 'graben') {
          return [...m.slice(0, -1),
                  { art: 'graben', punkte: [...letzte.punkte, p] }];
        }
        return [...m, { art: 'graben', punkte: [p] }];
      });
    }
    aufAenderung?.();
  }

  async function speichern() {
    setLaeuft(true); setMeldung(null);
    try {
      const r = await api.lageplanSpeichern(auftragId, {
        lat: lage!.mitte.lat, lng: lage!.mitte.lng, zoom: lage!.zoom,
        markierungen: mark, luftbild: schalter.luftbild,
        flurstuecke: schalter.grenzen,
      });
      setGespeichert(mark);
      setMeldung(r.hinweis ?? 'Ausschnitt und Markierungen sind gemerkt.');
    } catch (f: any) { setFehler(f?.fehler ?? String(f)); }
    finally { setLaeuft(false); }
  }

  /**
   * ALLES WEG - AUCH DAS GESPEICHERTE.
   *
   * Es geht durch DENSELBEN Weg wie „Ausschnitt merken" (`lageplanSpeichern`
   * mit einer leeren Liste) und nicht durch einen eigenen Löschweg. Ein
   * zweiter Weg über dieselbe Frage läuft beim ersten Nachtrag auseinander -
   * und dann räumt der eine ab, was der andere stehenlässt.
   *
   * DER AUSSCHNITT BLEIBT, WO ER IST. Gelöscht werden die MARKIERUNGEN;
   * Mitte, Stufe und die beiden Schalter sind die Ansicht, und die hat mit
   * dem Kreis nichts zu tun.
   */
  async function allesLoeschen() {
    setLoeschfrage(false);
    setMark([]);
    if (!gespeichert.length || !lage) { aufAenderung?.(); return; }
    setLaeuft(true); setMeldung(null); setFehler(null);
    try {
      await api.lageplanSpeichern(auftragId, {
        lat: lage.mitte.lat, lng: lage.mitte.lng, zoom: lage.zoom,
        markierungen: [], luftbild: schalter.luftbild,
        flurstuecke: schalter.grenzen,
      });
      setGespeichert([]);
      setMeldung('Die Markierungen sind gelöscht, auch die gespeicherten.');
      aufAenderung?.();
    } catch (f: any) { setFehler(f?.fehler ?? String(f)); }
    finally { setLaeuft(false); }
  }

  async function erzeugen() {
    setLaeuft(true); setMeldung(null); setFehler(null);
    try {
      /* ALLE 4 HAKEN GEHEN MIT.
       *
       * ORIGINALTEXT MARVIN (08.09.2026)
       *   „Lageplan: was oben angehakt ist (z. B. Straßennamen mit
       *    Hausnummern), muss beim Erzeugen mit auf das Blatt"
       *
       * Hier standen 2 von 4: `luftbild` und `flurstuecke`. Was der Server
       * nicht bekommt, kann er nicht zeichnen - und die Straßennamen, die auf
       * dem Bildschirm über dem Luftbild lagen, fehlten auf dem Papier an die
       * Gemeinde. Die Namen sind die des Servers (`flurstuecke` für die
       * Grenzen), damit hier keine zweite Sprache entsteht. */
      const r = await api.lapMaps(auftragId, {
        lat: lage!.mitte.lat, lng: lage!.mitte.lng, zoom: lage!.zoom,
        markierungen: mark, luftbild: schalter.luftbild,
        beschriftung: schalter.schrift,
        flurstuecke: schalter.grenzen,
        nummern: schalter.nummern,
      });
      /* WAS WIRKLICH AUF DEM BLATT STEHT, STEHT IN DER MELDUNG - und zwar
         aus der ANTWORT und nicht aus den Haken daneben: ein Dienst, der
         nicht antwortet, lässt eine angehakte Lage weg, und dann sagte eine
         Meldung aus den Haken etwas, das auf dem Papier nicht steht. */
      const lagen = [
        r.mitLuftbild ? 'Luftbild' : r.mitStrassenkarte ? 'Straßenkarte' : null,
        r.mitBeschriftung ? 'Straßennamen' : null,
        r.mitGrenzen ? `Grenzen (${r.grenzstuecke})` : null,
        r.mitNummern ? 'Nummern' : null,
      ].filter(Boolean);
      setMeldung(`${r.dateiname} liegt im Auftragsordner`
        + (lagen.length ? ` (${lagen.join(', ')}).` : '.')
        + (r.hinweis ? ' ' + r.hinweis : ''));
      setGespeichert(mark);
      setD(await api.lageplan(auftragId));
      aufAenderung?.();
    } catch (f: any) { setFehler(f?.fehler ?? String(f)); }
    finally { setLaeuft(false); }
  }

  async function umgebungSuchen() {
    setUmgLaeuft(true); setUmg(null);
    try {
      setUmg(await api.umgebung(auftragId, mark));
    } catch (f: any) {
      setUmg({ ok: false, meldung: f?.fehler ?? String(f) });
    } finally { setUmgLaeuft(false); }
  }

  async function umgebungAblegen() {
    setUmgLaeuft(true);
    try {
      const r = await api.umgebungAblegen(auftragId, {
        markierungen: mark,
        grade: (umg?.bilder ?? []).map((b: any) => b.grad),
      });
      setMeldung(`${r.dateien.length} Ansichten liegen im Ordner „${r.ordner}".`
        + (r.hinweis ? ' ' + r.hinweis : ''));
      aufAenderung?.();
    } catch (f: any) { setFehler(f?.fehler ?? String(f)); }
    finally { setUmgLaeuft(false); }
  }

  /**
   * Eine Ansicht weiterdrehen - UND DAS BILD DAZU HOLEN.
   *
   * ORIGINALTEXT MARVIN (08.09.2026)
   *   „bei dem streetview lassen sich die bilder nicht drehen im winkel"
   *
   * HIER STAND NUR DIE ZAHL. Die Pfeile haben `grad` weitergezählt und das
   * Bild stehenlassen; darunter erklärte ein Satz, dass die Vorschau „noch
   * den alten Winkel" zeigt. Das war ehrlich und trotzdem falsch: **ein
   * Knopf, der eine Zahl ändert und nicht das Bild, sieht aus wie ein
   * kaputter Knopf.**
   *
   * FÜNFZEHN GRAD UND NICHT NEUNZIG. „Im Winkel" heisst: den Ausschnitt
   * treffen. Mit neunzig Grad kommt man nach vier Klicks wieder dort an, wo
   * man angefangen hat, und dazwischen liegt nichts - das ist keine Drehung,
   * das ist ein Wechsel zwischen vier festen Ansichten.
   *
   * ES WIRD IMMER DER GANZE SATZ GEHOLT. Der Server rechnet die vier
   * Richtungen aus EINEM Panorama; einzeln nachzuladen hiesse, vier Anfragen
   * über vier verschiedene Panoramen zu führen, und dann stammt Bild 2 aus
   * einer anderen Aufnahme als Bild 1.
   */
  async function drehen(i: number, um: number) {
    const alt = umg?.bilder as any[] | undefined;
    if (!alt) return;
    const grade = alt.map((b: any, j: number) =>
      j === i ? ((Number(b.grad) + um) % 360 + 360) % 360 : Number(b.grad));
    /* DIE ZAHL STEHT SOFORT DA, DAS BILD KOMMT NACH. Ein Knopf, der eine
       Sekunde lang gar nichts tut, wird ein zweites Mal gedrückt. */
    setUmg((u: any) => u && ({
      ...u,
      bilder: u.bilder.map((b: any, j: number) => ({ ...b, grad: grade[j] })),
    }));
    setUmgLaeuft(true);
    try {
      const neu = await api.umgebung(auftragId, mark, grade);
      setUmg(neu);
    } catch (f: any) {
      /* DER ALTE STAND BLEIBT STEHEN, und der Grund steht daneben. Eine
         Drehung, die das Bild wegnimmt, ist schlimmer als eine, die es nicht
         austauscht. */
      setFehler(f?.fehler ?? String(f));
    } finally { setUmgLaeuft(false); }
  }

  /* WAS AUF DEM BLATT LIEGEN WIRD, IN DREI ZAHLEN.
     ------------------------------------------------------------------------
     ORIGINALTEXT MARVIN (08.09.2026)
       „Lageplan: oben Anzahl geplanter Gruben, geplanter Gräben, Länge der
        Gräben"

     Gerechnet wird in `teile/grabenlaenge.ts` und nicht hier: die Formel
     steht wörtlich ein zweites Mal im Server, und in einer Datei mit JSX
     wäre sie nur über eine Textsuche zu erreichen. */
  const zahlen = lageplanZahlen(mark as any);
  const grubenZahl = zahlen.gruben;
  const grabenZahl = zahlen.graeben;
  /* VERGLICHEN WIRD DER INHALT, NICHT DIE ADRESSE DES OBJEKTS. `mark` wird bei
     jeder Änderung neu gebaut; zwei Listen mit denselben Punkten darin sind
     für `===` trotzdem verschieden, und dann stünde „noch nicht gespeichert"
     dauerhaft da. Dieselbe Lehre wie bei `useEntwurf` am 31.08.2026. */
  const offen = JSON.stringify(mark) !== JSON.stringify(gespeichert);

  return (
    <>
      {/* WOHER DER PUNKT KOMMT. Er steht ÜBER der Karte und nicht darunter:
          wer zeichnet, soll es wissen, bevor er den ersten Klick setzt. Der
          ganze Absatz steht in `teile/Punktgenauigkeit.tsx`. */}
      <Punktgenauigkeit
        auftragId={auftragId} anschrift={d.anschrift}
        genauigkeit={d.punktgenauigkeit} hinweis={d.punkthinweis}
        fertig={() => {
          // NEU LADEN, NICHT NUR DEN KASTEN WEGNEHMEN. Mit dem neuen Punkt
          // wandert die Karte, und das entscheidet der Server.
          api.lageplan(auftragId).then((r) => {
            setD(r);
            if (r.mitte) setLage({ mitte: r.mitte, zoom: r.zoom });
          }).catch(() => {});
        }} />

      {/* DIE DREI ZAHLEN STEHEN ÜBER DER KARTE.
       *
       * ORIGINALTEXT MARVIN (08.09.2026)
       *   „Lageplan: oben Anzahl geplanter Gruben, geplanter Gräben, Länge
       *    der Gräben"
       *
       * Sie standen UNTER der Karte, und die Länge stand gar nicht da. Wer
       * zeichnet, sieht auf die Karte; was darunter steht, liest er, wenn er
       * fertig ist - **und die Länge ist die Zahl, mit der man die
       * Aufgrabungsanzeige gegenprüft, also die, die man WÄHREND des
       * Zeichnens braucht.**
       *
       * SIE BLEIBEN AUCH UNTEN STEHEN, mit dem Speicherstand daneben: dort
       * wird gehandelt („Ausschnitt merken"), hier wird gezeichnet. Es ist
       * dieselbe Rechnung an beiden Stellen und keine zweite Fassung.
       *
       * UND SIE IST EINE ANZEIGE UND KEINE VERMESSUNG. Der Satz steht seit
       * dem 20.08.2026 unter jeder dieser Karten und gilt für die Meterzahl
       * genauso: ein Mausklick ist so genau wie der rote Kreis, den der
       * Innendienst bisher in Acrobat gesetzt hat. */}
      <div className="reihe" style={{ margin: '0 0 8px' }}>
        <span className="merker">{grubenZahl} Grube{grubenZahl === 1 ? '' : 'n'}</span>
        <span className="merker">{grabenZahl} Graben</span>
        <span className="merker">{meterText(zahlen.meter)} Graben</span>
        <span className="klein leise">
          gezeichnet, symbolisch - nicht vermessen
        </span>
      </div>

      {/* EINE KARTE FÜR BEIDE BEREICHE.
       *
       * ORIGINALTEXT MARVIN (04.09.2026)
       *   „beide Bereiche mit Lageplan vorbereiten für die gemeinde und
       *    planung für den tiefbau hat die gleiche kartenstruktur mit allem.
       *    es unterscheidet sich nur in dem was markiert ist beim starten und
       *    die symbole zur anzeichnung!"
       *
       * Hier standen bis .107 die vier Haken, das Nachladen der Grenzen, das
       * Messen der Spalte und zehn Vorlagen an `Tiefbaukarte` - und dieselben
       * vier Dinge noch einmal in der Tiefbauplanung. Sie stehen jetzt in
       * `teile/Kartenflaeche.tsx`, einmal. **Was dieser Bereich beisteuert,
       * sind genau die zwei Dinge, die Marvin nennt:** womit er beginnt
       * (`START_LAGEPLAN`) und was gezeichnet wird (die Knöpfe unten und
       * `kinder`). */}
      <Kartenflaeche
        auftragId={auftragId}
        lage={lage} setzeLage={setLage}
        start={START_LAGEPLAN}
        daten={d}
        wunschBreite={breite} wunschHoehe={hoehe}
        aufSchalter={(x) => { setSchalter(x); aufAenderung?.(); }}
        aufFlurstand={setFlurstand}
        werkzeuge={<>
          {/* DIE SYMBOLE ZUR ANZEICHNUNG - hier ein Kreis und ein Weg in
              Acrobat-Rot, wörtlich das, was der Innendienst seit Jahren von
              Hand in Acrobat setzt. */}
          <button className={'knopf' + (werkzeug === 'grube' ? ' haupt' : '')}
                  onClick={() => setWerkzeug('grube')} disabled={laeuft}>
            Montagegrube
          </button>
          <button className={'knopf' + (werkzeug === 'graben' ? ' haupt' : '')}
                  onClick={() => setWerkzeug('graben')} disabled={laeuft}>
            Graben
          </button>
          <button className="knopf" disabled={laeuft || !mark.length}
                  onClick={() => { setMark((m) => m.slice(0, -1));
                                   aufAenderung?.(); }}>
            zurück
          </button>
          {/* EIN KNOPF, DER AUSSIEHT, ALS LÖSCHE ER, MUSS LÖSCHEN.
           *
           * ORIGINALTEXT MARVIN (03.09.2026)
           *   „er ht schon wieder einen kreis zwischengespeichert angezeigt
           *    obwohl ich nichts vorbereitet habe."
           *
           * „Alles weg" räumte den BILDSCHIRM, die gespeicherte Zeile blieb
           * stehen, und beim nächsten Öffnen war der Kreis wieder da. Ein
           * Knopf mit dieser Aufschrift verspricht das Löschen; er löst es
           * jetzt ein. DIE RÜCKFRAGE GEHÖRT DAZU - eine Zeichnung, die ein
           * Fehlklick aus der Akte nimmt, wäre in einem Antrag an die
           * Gemeinde die teuerste Sorte Verlust - und sie kommt NUR, wenn
           * wirklich etwas gespeichert ist: eine Rückfrage, die immer gleich
           * beantwortet wird, ist keine Sicherung, sondern eine
           * Gewöhnung. */}
          <button className="knopf"
                  disabled={laeuft || (!mark.length && !gespeichert.length)}
                  onClick={() => {
                    if (gespeichert.length) { setLoeschfrage(true); return; }
                    setMark([]); aufAenderung?.();
                  }}>
            Alles weg
          </button>
          {/* DIE ZAHL STEHT, WO DIE KNÖPFE STEHEN.
              Sie stand unter der Karte, und dorthin sieht niemand, der oben
              auf „Alles weg" drückt. **Eine Auskunft, die zu dem Knopf
              gehört, steht neben dem Knopf.** */}
          <span className="klein leise" style={{ whiteSpace: 'nowrap' }}>
            {gespeichert.length
              ? `${gespeichert.length} gespeichert`
              : 'nichts gespeichert'}
            {offen && <>, <b style={{ fontWeight: 600 }}>offen</b></>}
          </span>
        </>}
        aufKlick={setzen}
        kinder={(nach, mpp) => (
          <>
            {mark.map((m, i) => {
              if (m.art === 'grube') {
                const q = nach(m.punkte[0]);
                // DER RING HAT EINE GRÖSSE IM GELÄNDE, keine in Bildpunkten
                // (Marvin, 02.09.2026: „der ring ist immer in der gleichen
                // größe und passt sich dem zoom nicht an"). Dieselbe Rechnung
                // steht im Druck, sonst zeigt das Papier an die Gemeinde einen
                // anderen Kreis als der Bildschirm, an dem er gesetzt wurde.
                const r = grubenRadius(mpp);
                return (
                  <g key={i}>
                    <circle cx={q.x} cy={q.y} r={r} className="lpmark halo" />
                    <circle cx={q.x} cy={q.y} r={r} className="lpmark" />
                  </g>
                );
              }
              const weg = m.punkte.map((p) => nach(p));
              const d2 = weg.map((q, j) =>
                `${j ? 'L' : 'M'}${q.x.toFixed(1)} ${q.y.toFixed(1)}`).join(' ');
              return (
                <g key={i}>
                  <path d={d2} className="lpmark halo" />
                  <path d={d2} className="lpmark" />
                  {weg.map((q, j) => (
                    <circle key={j} cx={q.x} cy={q.y} r={3.2} className="lpgriff" />
                  ))}
                </g>
              );
            })}
          </>
        )}
      />

      <p className="klein leise" style={{ margin: '8px 0 0' }}>
        <b>Ziehen</b> verschiebt die Karte, <b>Klick</b> setzt eine Markierung.
        Die Markierung ist symbolisch und nicht vermessen, genau wie der rote
        Kreis, den der Innendienst bisher in Acrobat gesetzt hat.
        {flurstand.laeuft && <>, Grenzen werden geholt …</>}
        {flurstand.meldung && <>, {flurstand.meldung}</>}
      </p>

      <div className="reihe" style={{ marginTop: 10, flexWrap: 'wrap' }}>
        <span className="klein leise">
          {grubenZahl} Grube{grubenZahl === 1 ? '' : 'n'},
          {' '}{grabenZahl} Graben,{' '}{meterText(zahlen.meter)}
          {/* WAS GESPEICHERT IST, UND WAS NUR AUF DEM SCHIRM STEHT.
              Marvin, 03.09.2026: „es sind «alte kreise» ... irgendwo
              gecacht". Sie sind gespeichert und nicht gecacht.
              DIE KURZFASSUNG STEHT SEIT .102 OBEN NEBEN „Alles weg" - dort
              wird gehandelt, und dorthin sieht, wer handelt. Hier steht der
              ganze Satz für den, der ihn sucht. */}
          {', '}{gespeichert.length} gespeichert
          {offen && (
            <>, <b style={{ fontWeight: 600 }}>noch nicht gespeichert</b>
              {' '}(gültig mit „Ausschnitt merken" oder „LAP_Maps erzeugen")</>
          )}
          {/* UEBER `datum()`. `toLocaleDateString('de-DE')` ohne Optionen
              laesst die fuehrende Null weg: 9.9.2026 statt 09.09.2026. */}
          {d.erzeugtAm && <>, zuletzt erzeugt am {datum(d.erzeugtAm)}</>}
        </span>
        <div className="wachsen" />
        <button className="knopf" onClick={() => setOrtSuchen((x) => !x)} disabled={laeuft}>
          {ortSuchen ? 'Suche schließen' : 'Standort falsch? Anschrift suchen'}
        </button>
        <button className="knopf" onClick={speichern} disabled={laeuft}>
          Ausschnitt merken
        </button>
        <button className="knopf haupt" onClick={erzeugen} disabled={laeuft}>
          {laeuft ? 'Einen Moment' : 'LAP_Maps erzeugen'}
        </button>
      </div>

      {ortSuchen && (
        <div className="hinweis" style={{ marginTop: 10 }}>
          <b>Standort neu suchen</b>
          <div className="klein">Gibt es die Straße in dieser PLZ mehrmals, den Ortsteil dazuschreiben. Der gefundene Punkt gilt dann für Karte, Umgebungsbilder und LAP_Maps.</div>
          <Adresssuche auftragId={auftragId} anschrift={d.anschrift} aufschrift="Standort suchen"
            fertig={() => {
              setOrtSuchen(false);
              api.lageplan(auftragId).then((r) => { setD(r); if (r.mitte) setLage({ mitte: r.mitte, zoom: r.zoom }); }).catch(() => {});
              aufAenderung?.();
            }} />
        </div>
      )}

      {/* DIE RÜCKFRAGE VOR DEM WIRKLICHEN LÖSCHEN.
       *
       * Sie kommt nur, wenn etwas GESPEICHERT ist - und dann sagt sie, wie
       * viel. **Eine Rückfrage ohne Zahl beantwortet die eine Frage nicht,
       * die man vor dem Klicken hat.** */}
      {loeschfrage && (
        <Dialog titel="Alle Markierungen löschen?" laeuft={laeuft}
                zu={() => setLoeschfrage(false)}>
          <p className="klein">
            An diesem Lageplan {gespeichert.length === 1
              ? 'ist eine Markierung gespeichert'
              : `sind ${gespeichert.length} Markierungen gespeichert`}.
            Sie werden mit gelöscht, nicht nur vom Bildschirm genommen.
          </p>
          <p className="klein leise">
            Der Ausschnitt bleibt, wo er ist. Eine bereits erzeugte
            LAP_Maps liegt ebenfalls weiter im Auftragsordner - sie wird erst
            beim nächsten Erzeugen ersetzt.
          </p>
          <div className="reihe" style={{ marginTop: 10 }}>
            <div className="wachsen" />
            <button className="knopf" onClick={() => setLoeschfrage(false)}
                    disabled={laeuft}>Abbrechen</button>
            <button className="knopf gefahr" onClick={allesLoeschen}
                    disabled={laeuft}>
              {laeuft ? 'Einen Moment' : 'Wirklich löschen'}
            </button>
          </div>
        </Dialog>
      )}

      {meldung && <div className="hinweis erledigt" style={{ marginTop: 8 }}>{meldung}</div>}

      {/* DIE UMGEBUNG.
       *
       * ORIGINALTEXT MARVIN (24.08.2026)
       *   „baue die „umgebung" mit streetview ein."
       *
       * Vier Ansichten für den Tiefbauer. Der Anhaltspunkt ist die
       * MONTAGEGRUBE, nicht die Adresse: die zeigt auf die Hausmitte, und wird
       * die Grube ein Stück versetzt, zeigt kein einziges Bild darauf. */}
      <div className="karte" style={{ marginTop: 12 }}>
        <h3>
          Umgebung
          <span className="weg">4 Ansichten für den Tiefbauer</span>
        </h3>
        <div className="leib">
          <div className="reihe" style={{ marginBottom: 8 }}>
            <button className="knopf" onClick={umgebungSuchen} disabled={umgLaeuft}>
              {umgLaeuft ? 'Einen Moment' : 'Street View suchen'}
            </button>
            {umg?.ok && (
              <button className="knopf haupt" onClick={umgebungAblegen}
                      disabled={umgLaeuft}>
                In den Ordner „Umgebung" legen
              </button>
            )}
            <div className="wachsen" />
            <span className="klein leise">
              {grubenZahl
                ? 'Anhaltspunkt: die erste Montagegrube.'
                : 'Ohne Grube gilt die Adresse als Anhaltspunkt.'}
            </span>
          </div>

          {umg && !umg.ok && (
            <div className="hinweis warnung" style={{ marginBottom: 0 }}>
              {umg.meldung}
            </div>
          )}

          {umg?.ok && (
            <>
              <div className={'hinweis' + (umg.fremd ? ' warnung' : '')}
                   style={{ marginBottom: 8 }}>
                {umg.meldung}
              </div>
              <div className="svgitter">
                {umg.bilder.map((b: any, i: number) => (
                  <div key={b.name} className="svbild">
                    {b.bild
                      ? <img src={b.bild} alt={b.name} />
                      : <div className="svleer">{b.grund ?? 'kein Bild'}</div>}
                    <div className="reihe klein">
                      <b>{b.name}</b>
                      <div className="wachsen" />
                      <span className="leise">{b.grad}°</span>
                      <button className="knopf klein" disabled={umgLaeuft}
                              title="15 Grad nach links"
                              onClick={() => drehen(i, -15)}>&larr;</button>
                      <button className="knopf klein" disabled={umgLaeuft}
                              title="15 Grad nach rechts"
                              onClick={() => drehen(i, 15)}>&rarr;</button>
                    </div>
                  </div>
                ))}
              </div>
              {/* DER SATZ „die Vorschau zeigt noch den alten Winkel" IST WEG,
                  weil er nicht mehr stimmt: das Bild wird mitgedreht. Was
                  bleibt, ist die eine Auskunft, die man beim Hinsehen NICHT
                  bekommt - dass die abgelegten Bilder grösser sind. */}
              <p className="klein leise" style={{ marginBottom: 0 }}>
                Die Pfeile drehen um 15 Grad und holen die Ansicht neu.
                Abgelegt wird sie in voller Größe.
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* HIER STAND `kasten` - UND DIESELBE RECHNUNG NOCH EINMAL IN DER PLANUNG,
 * unter dem Namen `kartenkasten`, Zeichen für Zeichen gleich.
 *
 * Sie steht jetzt in `teile/Kartenflaeche.tsx`. **Zwei Rahmen, die um
 * Bruchteile auseinanderlaufen, holen zwei verschiedene Kästen von Grenzen -
 * und dann zeigt die eine Karte ein Flurstück, das die andere nicht hat.**
 * Dieselbe Lehre wie bei `ausschnittRahmen` auf dem Server, wo dieselbe
 * Rechnung in .107 zum dritten Mal hätte dastehen müssen.
 */

